import * as React from "react"
import { Check, ChevronsUpDown, Plus, X, Trash2 } from "lucide-react"
import { cn } from "../../lib/utils"
import { Button } from "./button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover"

export function Combobox({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Seleccionar...",
  emptyText = "No hay opciones",
  allowCustom = true,
  allowClear = true,
  allowDelete = false,
  onDeleteOption = null,
  className
}) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")

  const handleSelect = (selectedValue) => {
    onChange(selectedValue === value ? "" : selectedValue)
    setOpen(false)
    setInputValue("")
  }

  const handleClear = (e) => {
    e?.stopPropagation?.()
    onChange("")
    setOpen(false)
    setInputValue("")
  }

  const handleAddCustom = () => {
    if (inputValue.trim()) {
      onChange(inputValue.trim())
      setOpen(false)
      setInputValue("")
    }
  }

  const handleDeleteOption = (e, option) => {
    e.stopPropagation()
    if (onDeleteOption) {
      onDeleteOption(option)
    }
  }

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(inputValue.toLowerCase())
  )

  const showAddOption = allowCustom && 
    inputValue.trim() && 
    !options.some(opt => opt.toLowerCase() === inputValue.toLowerCase().trim())

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between font-normal group", className)}
        >
          <span className="truncate">
            {value || <span className="text-slate-400">{placeholder}</span>}
          </span>
          <div className="flex items-center gap-1 ml-2 shrink-0">
            {allowClear && value && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClear}
                onKeyDown={(e) => e.key === 'Enter' && handleClear(e)}
                className="h-4 w-4 rounded-full hover:bg-slate-200 flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity"
                aria-label="Borrar valor"
              >
                <X className="h-3 w-3" />
              </span>
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput 
            placeholder={`Buscar o escribir...`}
            value={inputValue}
            onValueChange={setInputValue}
          />
          <CommandList>
            {/* Clear option when value exists */}
            {allowClear && value && !inputValue && (
              <>
                <CommandGroup>
                  <CommandItem
                    onSelect={handleClear}
                    className="cursor-pointer text-red-600 hover:text-red-700"
                  >
                    <X className="mr-2 h-4 w-4" />
                    <span>Borrar selección</span>
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            {filteredOptions.length === 0 && !showAddOption && !value && (
              <CommandEmpty>{emptyText}</CommandEmpty>
            )}
            {showAddOption && (
              <CommandGroup heading="Añadir nuevo">
                <CommandItem
                  onSelect={handleAddCustom}
                  className="cursor-pointer"
                >
                  <Plus className="mr-2 h-4 w-4 text-green-600" />
                  <span>Añadir "<strong>{inputValue.trim()}</strong>"</span>
                </CommandItem>
              </CommandGroup>
            )}
            {filteredOptions.length > 0 && (
              <CommandGroup heading="Opciones existentes">
                {filteredOptions.map((option) => (
                  <CommandItem
                    key={option}
                    value={option}
                    onSelect={() => handleSelect(option)}
                    className="cursor-pointer group/item"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 shrink-0",
                        value === option ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="flex-1 truncate">{option}</span>
                    {allowDelete && onDeleteOption && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => handleDeleteOption(e, option)}
                        onKeyDown={(e) => e.key === 'Enter' && handleDeleteOption(e, option)}
                        className="ml-2 h-5 w-5 rounded hover:bg-red-100 flex items-center justify-center opacity-0 group-hover/item:opacity-100 transition-opacity"
                        title="Eliminar opción"
                      >
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
